import { useState, useEffect, useCallback } from 'react';
import { useCases } from '../../core/di/useCases';
import { logger } from '../utils/logger';

/**
 * Hook qui récupère et écoute les détails d'un post à partir de son ID en respectant la Clean Architecture
 * @param {string} postId - L'identifiant du post à récupérer
 * @returns {Object} - Le post et ses données associées
 */
export const usePostDetails = (postId) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fonction pour charger les détails du post
  const fetchPostDetails = useCallback(async () => {
    if (!postId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Utiliser le use case GetPost au lieu d'accéder directement à Firebase
      const response = await useCases.getPost.execute({ postId });

      if (response.success) {
        setPost(normalizePost(response.data));
      } else {
        setPost(null);
        setError(new Error(response.error || 'Post non trouvé'));
      }
    } catch (err) {
      logger.error('Erreur lors de la récupération du post:', err);
      setError(err);
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  // Configuration initiale et abonnement aux mises à jour
  useEffect(() => {
    if (!postId) {
      setLoading(false);
      return;
    }

    // Charger initialement les données
    fetchPostDetails();

    // Configurer un abonnement temps réel spécifique à ce post via le use case
    const unsubscribe = useCases.subscribeToPost.execute({
      postId,
      onUpdate: (updatedPost) => {
        setPost(normalizePost(updatedPost));
      },
      onError: (err) => {
        logger.error('Erreur dans l\'abonnement au post:', err);
        // Ne pas setter d'erreur ici car nous avons déjà chargé les données initialement
      }
    });

    return () => {
      // Nettoyer l'abonnement lors du démontage
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [postId, fetchPostDetails]);

  return { post, loading, error };
};

/**
 * Normalise la structure de localisation pour garantir address, lat, lng
 */
function normalizeLocation(rawLocation) {
  if (!rawLocation) return {};
  return {
    address: rawLocation.address || rawLocation.formattedAddress || rawLocation.label || '',
    lat: rawLocation.latitude || rawLocation.lat || (rawLocation.coordinates && rawLocation.coordinates.latitude),
    lng: rawLocation.longitude || rawLocation.lng || (rawLocation.coordinates && rawLocation.coordinates.longitude),
    ...rawLocation
  };
}

/**
 * Normalise le post pour garantir un format homogène
 */
function normalizePost(rawPost) {
  return {
    ...rawPost,
    location: normalizeLocation(rawPost.location || rawPost.activity?.location),
    // Ajoute d'autres mappings si besoin (dates, participants, etc.)
  };
} 